import React, { useEffect, useRef, useState } from 'react';
import { Autocomplete, TextField, CircularProgress, Chip } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import channelService, { type ChannelMember } from '../../../services/channelService';
import type { RuleFlowRecipient } from '../../../services/channelRuleFlowsService';

export interface RecipientPickerProps {
  channelId: string;
  /** Selected recipients — either a channel member's email or a manually typed one
   * (base_shay's approvals.py allows any channel member / any company user as approver, per
   * TDD §9, so no admin-only restriction is applied here). A null email (source: 'missing')
   * shows as an empty/unfilled tag the admin can still remove or replace. */
  value: RuleFlowRecipient[];
  onChange: (recipients: RuleFlowRecipient[]) => void;
  disabled?: boolean;
  label?: string;
}

/**
 * Business_rule_canvas branch (PRISM7_CHANNEL_RULES_CANVAS_TDD.md §8.2) — dropdown of all channel
 * members, via the already-existing GET /api/v1/channels/{id}/members (no backend change
 * needed), plus a manual-email freeSolo fallback for a recipient who isn't a channel member.
 * Used for any RuleNode's recipients[] entry (e.g. an approval ACTION's approver list).
 *
 * Internally works with plain email strings (what Autocomplete's multi-select actually needs),
 * translating to/from RuleFlowRecipient[] at the edges: existing entries keep their original
 * role/source when their email is unchanged; anything newly typed/picked here is
 * source: 'user_selected', since that's what this picker itself produces per TDD §5.2's
 * anchor model (only field_block/inline entries carry a text anchor, and those come from
 * parsing, never from this component).
 */
const RecipientPicker: React.FC<RecipientPickerProps> = ({
  channelId,
  value,
  onChange,
  disabled = false,
  label = 'Recipients',
}) => {
  const [members, setMembers] = useState<ChannelMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // FIX 2b (frontend handover) — clicking a "missing" pill focuses the input so the dropdown
  // opens (openOnFocus below), letting the admin pick a real recipient; that pick is added as a
  // new entry via the normal handleChange path, same as typing/selecting from scratch.
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    if (!channelId) return;
    setLoading(true);
    setError(null);
    channelService
      .getChannelMembersAll(channelId)
      .then((result) => {
        if (!cancelled) setMembers(result);
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load channel members. You can still type an email directly.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [channelId]);

  const options = members.map((m) => m.email).filter(Boolean);
  const emailValues = value.map((r) => r.email || '');

  const handleChange = (newValue: (string | null)[]) => {
    const nextRecipients: RuleFlowRecipient[] = newValue.map((email) => {
      // Matched against (r.email || '') rather than r.email directly — an unresolved recipient's
      // real email is null, but its slot in Autocomplete's own flat value array (emailValues
      // above) is '' ; comparing the raw values missed this match, silently dropping that
      // entry's role/source (e.g. "Assigned Manager"/'missing') the moment ANY other recipient
      // in the list changed, even though this specific entry wasn't touched.
      const existing = value.find((r) => (r.email || '') === email);
      if (existing) return existing;
      return { email, source: 'user_selected' };
    });

    // Bug found live — an unresolved ("required") recipient had no way to ever clear itself:
    // adding real recipients alongside it left the placeholder (and its warning) on screen
    // forever, since nothing in this array was ever removed by picking someone else. The moment
    // at least one recipient here is actually resolved, any still-unresolved placeholder(s) are
    // no longer the only assignment for this node — auto-drop them so the admin doesn't have to
    // separately dismiss each one after adding a real recipient. Only fires while at least one
    // resolved recipient remains (never strips the last/only entries down to nothing).
    const hasResolvedRecipient = nextRecipients.some((r) => r.email);
    onChange(hasResolvedRecipient ? nextRecipients.filter((r) => r.email) : nextRecipients);
  };

  return (
    <Autocomplete
      multiple
      freeSolo
      size="small"
      options={options}
      value={emailValues}
      disabled={disabled}
      loading={loading}
      onChange={(_event, newValue) => handleChange(newValue as string[])}
      openOnFocus
      getOptionLabel={(option) => {
        const member = members.find((m) => m.email === option);
        return member ? `${member.name} (${member.email})` : String(option);
      }}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          const recipient = value[index];
          // FIX 2b (frontend handover) — an unresolved recipient (parser found a role like
          // "senior IT manager" but no matching email) must read as visibly different from a
          // real assignment.
          //
          // Gated on the email itself being unresolved (not on source === 'missing' alone) — a
          // real flow surfaced a recipient with a null email but a source other than 'missing'
          // (e.g. 'user_selected'/'field_block'/'inline' with no email attached), which fell
          // through to the plain, dismissible "Unassigned" chip below even though there was
          // equally nothing to unassign. Any recipient with no email needs someone picked,
          // regardless of which source tag the backend attached to it.
          //
          // Bug found live — this pill originally had no onDelete at all (the reasoning being
          // "there's nothing to unassign"), but handleChange only ever APPENDS a newly picked
          // recipient as a new array entry; it can never replace this specific slot. With no way
          // to dismiss it either, admins hit a dead end: adding real recipients alongside it left
          // this placeholder (and its "required" warning) on screen forever, no matter how many
          // real people they added. Spreading getTagProps here reuses Autocomplete's own removal
          // handling (same mechanism the plain chip below already relies on) so the admin can
          // clear this slot once it's been superseded by the recipients they actually added.
          //
          // handleChange's own auto-clear (see its doc) only fires as a side effect of an actual
          // add/remove — a node whose recipients array already arrives from the backend in this
          // mixed state (real emails alongside an unresolved one) never goes through handleChange
          // at all just from being viewed, so that placeholder kept showing indefinitely. This is
          // a purely cosmetic suppression for that case: if a resolved recipient already exists
          // anywhere in this list, an unresolved one is simply not rendered — no onChange call,
          // no network write, nothing in the underlying data changes. An actual edit still goes
          // through handleChange's real auto-clear, which persists the removal for good.
          const hasResolvedElsewhere = value.some((r) => r.email);
          if (recipient && !recipient.email && hasResolvedElsewhere) {
            return null;
          }
          if (recipient && !recipient.email) {
            return (
              <Chip
                size="small"
                icon={<WarningAmberIcon fontSize="small" />}
                label={`${recipient.role || 'Recipient'} — required, pick someone`}
                color="warning"
                variant="outlined"
                {...getTagProps({ index })}
                onClick={() => inputRef.current?.focus()}
                key={`missing-${index}`}
              />
            );
          }
          return (
            <Chip size="small" label={option || 'Unassigned'} {...getTagProps({ index })} key={option || index} />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={inputRef}
          label={label}
          placeholder="Pick a channel member or type an email"
          error={Boolean(error)}
          helperText={error || undefined}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default RecipientPicker;
